<script setup lang="ts">
/**
 * Grande capture d'écran des pages charte « apple » (Karim 2026-10-18) :
 * titre et texte centrés, puis la capture dans un cadre navigateur sobre
 * (barre d'adresse grise, points gris), ombre douce. Sert au tableau de bord
 * des associations sur les pages de secteur.
 */
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";

const props = withDefaults(
  defineProps<{
    titre: string;
    description?: string;
    image: string;
    largeur: number;
    hauteur: number;
    alt: string;
    adresse?: string;
    fond?: "blanc" | "gris";
  }>(),
  { description: undefined, adresse: "dashboard.donoor.org", fond: "blanc" },
);
</script>

<template>
  <section class="px-4 py-20 sm:px-8 lg:py-28" :class="props.fond === 'gris' ? 'bg-[#f5f5f7]' : 'bg-white'">
    <div class="mx-auto max-w-[1120px]">
      <div class="mx-auto mb-12 max-w-[820px] text-center">
        <h2 class="pa-reveal mb-5 text-[32px] text-[#1d1d1f] [text-wrap:balance] sm:text-[48px]" :style="TITRE_SECTION_APPLE">
          {{ props.titre }}
        </h2>
        <p v-if="props.description" class="pa-reveal mx-auto max-w-[680px] text-[17px] font-medium leading-[1.55] text-[#424245] sm:text-[19px]">
          {{ props.description }}
        </p>
      </div>
      <div v-if="$slots.default" class="pa-reveal mb-10 flex flex-wrap justify-center gap-3">
        <slot />
      </div>
      <figure class="pa-reveal m-0 overflow-hidden rounded-[18px] bg-white shadow-[0_0_0_1px_#e8e8ed,0_40px_90px_-40px_rgba(0,0,0,0.35)]" style="--d: 100ms">
        <div class="barre" aria-hidden="true">
          <span class="points"><i /><i /><i /></span>
          <span class="adresse">
            <UIcon name="i-lucide-lock" class="size-2.5 text-[#6e6e73]" />
            {{ props.adresse }}
          </span>
          <span />
        </div>
        <NuxtImg
          :src="props.image"
          :alt="props.alt"
          :width="props.largeur"
          :height="props.hauteur"
          sizes="xs:94vw sm:94vw md:94vw lg:1120px"
          loading="lazy"
          class="block h-auto w-full"
        />
      </figure>
    </div>
  </section>
</template>

<style scoped>
.barre {
  display: grid;
  grid-template-columns: 70px 1fr 70px;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  background: #fbfbfd;
  border-bottom: 1px solid #e8e8ed;
}
.points {
  display: flex;
  gap: 6px;
}
.points i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d2d2d7;
}
.adresse {
  justify-self: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  padding: 3px 16px;
  border-radius: 7px;
  background: #e8e8ed;
  font-size: 11px;
  color: #424245;
  letter-spacing: 0;
  white-space: nowrap;
}
@media (max-width: 640px) {
  .barre {
    grid-template-columns: 34px 1fr 10px;
    height: 24px;
    padding: 0 8px;
  }
  .points {
    gap: 3px;
  }
  .points i {
    width: 6px;
    height: 6px;
  }
  .adresse {
    padding: 1px 8px;
    font-size: 8px;
  }
}
</style>
